import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { Loader2, Mail, Send, Archive, CheckCircle2, Inbox, Paperclip } from "lucide-react";
import { toast } from "sonner";
import {
  adminListSupportTickets,
  adminGetSupportTicket,
  adminReplySupportTicket,
  adminSetSupportTicketStatus,
} from "@/lib/api/support-tickets.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/portal/admin/support-tickets")({
  head: () => ({
    meta: [
      { title: "Admin · Support tickets" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SupportTicketsPage,
});

type Mailbox = "all" | "admin" | "info";
type StatusFilter = "open" | "pending" | "closed" | "all";

function SupportTicketsPage() {
  const qc = useQueryClient();
  const list = useServerFn(adminListSupportTickets);
  const getOne = useServerFn(adminGetSupportTicket);
  const reply = useServerFn(adminReplySupportTicket);
  const setStatus = useServerFn(adminSetSupportTicketStatus);

  const [mailbox, setMailbox] = useState<Mailbox>("all");
  const [statusF, setStatusF] = useState<StatusFilter>("open");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);


  const listQ = useQuery({
    queryKey: ["admin-support-tickets"],
    queryFn: () => list(),
    refetchInterval: 30_000,
  });

  const threadQ = useQuery({
    queryKey: ["admin-support-ticket", selectedId],
    queryFn: () => getOne({ data: { id: selectedId! } }),
    enabled: !!selectedId,
  });

  const items = useMemo(() => {
    const all = listQ.data?.items ?? [];
    let filtered = all;
    if (mailbox !== "all") filtered = filtered.filter((i) => i.mailbox === mailbox);
    if (statusF !== "all") filtered = filtered.filter((i) => i.status === statusF);
    const needle = q.trim().toLowerCase();
    if (needle) {
      filtered = filtered.filter((i) =>
        [i.from_email, i.from_name, i.subject].filter(Boolean).some((v) =>
          String(v).toLowerCase().includes(needle),
        ),
      );
    }
    return filtered;
  }, [listQ.data, mailbox, statusF, q]);

  const selectTicket = (id: string) => {
    setSelectedId(id);
    const t = listQ.data?.items.find((i) => i.id === id);
    setSubject(t ? `Re: ${t.subject.replace(/^re:\s*/i, "")}` : "");
    setBody("");
    setFiles([]);
  };

  const fileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
      reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
      reader.readAsDataURL(file);
    });

  const replyMut = useMutation({
    mutationFn: async () => {
      const attachments = await Promise.all(
        files.map(async (f) => ({
          filename: f.name,
          contentBase64: await fileToBase64(f),
          contentType: f.type || "application/octet-stream",
        })),
      );
      return reply({ data: { id: selectedId!, subject, body, attachments } });
    },
    onSuccess: (r: any) => {
      if (r?.ok) {
        toast.success("Reply sent");
        setBody("");
        setFiles([]);
        qc.invalidateQueries({ queryKey: ["admin-support-ticket", selectedId] });
        qc.invalidateQueries({ queryKey: ["admin-support-tickets"] });
      } else {
        toast.error(`Send failed: ${r?.reason || "unknown"}`);
      }
    },
    onError: (e: any) => toast.error(e?.message || "Failed to send"),
  });


  const statusMut = useMutation({
    mutationFn: (s: "open" | "pending" | "closed") =>
      setStatus({ data: { id: selectedId!, status: s } }),
    onSuccess: () => {
      toast.success("Status updated");
      qc.invalidateQueries({ queryKey: ["admin-support-tickets"] });
      qc.invalidateQueries({ queryKey: ["admin-support-ticket", selectedId] });
    },
  });

  const selected = threadQ.data?.ticket;
  const messages = threadQ.data?.messages ?? [];

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      <header className="mb-4 flex items-center gap-3">
        <Inbox className="h-6 w-6 text-gold" />
        <div>
          <h1 className="text-2xl font-bold text-navy">Support tickets</h1>
          <p className="text-sm text-muted-foreground">
            Inbound emails to admin@ and info@nkadimengdannylcs.com. Reply here and it goes back
            to the sender from your branded domain.
          </p>
        </div>
      </header>

      <div className="mb-3 flex flex-wrap gap-2">
        {(["all", "admin", "info"] as Mailbox[]).map((m) => (
          <Button
            key={m}
            size="sm"
            variant={mailbox === m ? "default" : "outline"}
            onClick={() => setMailbox(m)}
          >
            {m === "all" ? "All mailboxes" : `${m}@`}
          </Button>
        ))}
        <span className="mx-2 h-8 w-px bg-border" />
        {(["open", "pending", "closed", "all"] as StatusFilter[]).map((s) => (
          <Button
            key={s}
            size="sm"
            variant={statusF === s ? "default" : "outline"}
            onClick={() => setStatusF(s)}
          >
            {s[0].toUpperCase() + s.slice(1)}
          </Button>
        ))}
        <Input
          placeholder="Search sender or subject…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="ml-auto w-full md:w-72"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(280px,360px)_1fr]">
        <div className="rounded-lg border bg-card">
          {listQ.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              No tickets. New inbound emails will appear here automatically.
            </div>
          ) : (
            <ul className="max-h-[70vh] divide-y overflow-y-auto">
              {items.map((t) => (
                <li key={t.id}>
                  <button
                    onClick={() => selectTicket(t.id)}
                    className={`w-full px-3 py-3 text-left transition-colors hover:bg-muted/50 ${
                      selectedId === t.id ? "bg-muted" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-navy">
                        {t.from_name || t.from_email}
                      </span>
                      <Badge
                        variant={t.status === "open" ? "default" : "outline"}
                        className="shrink-0 text-[10px]"
                      >
                        {t.status}
                      </Badge>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {t.mailbox}@ · {t.subject}
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {new Date(t.last_message_at).toLocaleString()}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border bg-card p-4">
          {!selected ? (
            <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
              <Mail className="mr-2 h-4 w-4" /> Select a ticket to view the conversation
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-navy">{selected.subject}</h2>
                  <p className="text-xs text-muted-foreground">
                    From <strong>{selected.from_email}</strong> to{" "}
                    <strong>{selected.mailbox}@nkadimengdannylcs.com</strong>
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => statusMut.mutate("pending")}
                    disabled={statusMut.isPending}
                  >
                    <CheckCircle2 className="mr-1 h-3 w-3" /> Pending
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => statusMut.mutate("closed")}
                    disabled={statusMut.isPending}
                  >
                    <Archive className="mr-1 h-3 w-3" /> Close
                  </Button>
                </div>
              </div>

              <div className="max-h-[45vh] space-y-3 overflow-y-auto rounded border bg-background p-3">
                {messages.map((m: any) => (
                  <div
                    key={m.id}
                    className={`rounded border p-3 text-sm ${
                      m.direction === "outbound"
                        ? "border-gold/40 bg-gold/5"
                        : "border-border bg-white"
                    }`}
                  >
                    <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        <strong>{m.direction === "outbound" ? "You" : m.from_email}</strong>{" "}
                        {m.direction === "outbound" ? "→" : "→"}{" "}
                        {m.direction === "outbound" ? m.to_email : `${selected.mailbox}@`}
                      </span>
                      <span>{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    <pre className="whitespace-pre-wrap font-sans text-sm text-navy">
                      {m.body_text || "(no body)"}
                    </pre>
                    {m.status === "failed" && (
                      <p className="mt-2 text-xs text-destructive">Send failed: {m.error}</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="space-y-2 border-t pt-3">
                <label className="text-xs font-semibold text-muted-foreground">Reply</label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject"
                />
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Type your reply…"
                  rows={6}
                />
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Attachments (max 5, 10MB total)
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 5))}
                    className="block w-full text-xs file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-xs file:font-medium"
                  />
                  {files.length > 0 && (
                    <ul className="flex flex-wrap gap-2 pt-1">
                      {files.map((f) => (
                        <li
                          key={f.name}
                          className="flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs"
                        >
                          <Paperclip className="h-3 w-3" />
                          {f.name}
                          <button
                            type="button"
                            onClick={() => setFiles((prev) => prev.filter((x) => x !== f))}
                            className="ml-1 text-muted-foreground hover:text-destructive"
                            aria-label={`Remove ${f.name}`}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex justify-end">

                  <Button
                    onClick={() => replyMut.mutate()}
                    disabled={!body.trim() || !subject.trim() || replyMut.isPending}
                  >
                    {replyMut.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="mr-2 h-4 w-4" />
                    )}
                    Send reply
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
